import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { ago, dateTime, initials, stageLabel } from "../lib/format";
import type { Contact, Member, Message, ReplyKind, Thread, WaTemplate } from "../lib/types";
import { useEntitlements } from "../lib/plans";
import { TemplateSender } from "../components/WhatsAppTemplates";
import { MessageMedia } from "../components/MessageMedia";
import { EmailComposer } from "../components/EmailComposer";
import { countLabel, Empty, ErrorText, Loading, PageHeader } from "../components/ui";
import { Icon } from "../components/icons";
import { registerWarmer } from "../lib/smooth";
import { useInboxCounts } from "../lib/inbox";

const threadsQuery = (orgId: string, base: string, filter: string, q = "") => ({
  queryKey: [orgId, "threads", filter, q],
  queryFn: () => {
    const p = new URLSearchParams(filter === "human" ? { mode: "human" } : filter === "mine" ? { assigned: "me", status: "active" } : { status: filter });
    if (q) p.set("q", q);
    return api<{ threads: Thread[] }>(`${base}/threads?${p}`);
  },
});

const threadQuery = (orgId: string, base: string, id: string) => ({
  queryKey: [orgId, "thread", id],
  queryFn: () => api<Thread & { messages: Message[] }>(`${base}/threads/${id}`),
});
registerWarmer("inbox", (qc, { orgId, base }) => { qc.prefetchQuery({ ...threadsQuery(orgId, base, "active"), staleTime: 5_000 }); });

const filters: [string, string][] = [["active", "Active"], ["human", "Needs a person"], ["mine", "Mine"], ["closed", "Closed"], ["all", "All"]];

export default function Inbox() {
  const { base, orgId } = useOrg();
  const { threadId } = useParams();
  const [filter, setFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  const qc = useQueryClient();
  const counts = useInboxCounts();
  const threads = useQuery({
    ...threadsQuery(orgId, base, filter, q),
    refetchInterval: 10_000,
    placeholderData: (prev) => prev,
  });
  const list = threads.data?.threads ?? [];
  return (
    <>
      <PageHeader title="Inbox" actions={<span className="muted small">{list.length > 0 && `${countLabel(list.length)} conversation${list.length === 1 ? "" : "s"}`}</span>} />
      <div className={`inbox${threadId ? " has-open" : ""}`}>
        <aside className="thread-list" aria-label="Conversations">
          <div className="thread-search">
            <input type="search" placeholder="Search conversations" aria-label="Search conversations" value={search} maxLength={100} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="thread-filters" role="tablist" aria-label="Show">
            {filters.map(([v, label]) => (
              <button key={v} role="tab" aria-selected={filter === v} className={filter === v ? "on" : ""} onClick={() => setFilter(v)}>
                {label}{v === "human" && (counts.data?.needs_person ?? 0) > 0 && <span className="count">{counts.data!.needs_person}</span>}
              </button>
            ))}
          </div>
          <div className="thread-scroll">
          {threads.isLoading && <Loading rows={4} />}
          {threads.data && list.length === 0 && <Empty>{q ? `Nothing matches "${q}".` : "No conversations here."}</Empty>}
          {list.map((t) => (
            <Link key={t.id} to={`/o/${orgId}/inbox/${t.id}`} className={`thread${t.id === threadId ? " active" : ""}${t.unread && t.id !== threadId ? " unread" : ""}`}
              onPointerEnter={() => { if (t.id !== threadId) qc.prefetchQuery({ ...threadQuery(orgId, base, t.id), staleTime: 3_000 }); }}>
              <span className="av" aria-hidden>{initials(who(t))}</span>
              <span className="thread-top"><b className="thread-who">{who(t)}</b>{t.unread && t.id !== threadId && <i className="unread-dot" aria-label="Unread" />}<time>{shortTime(t.last_message_at)}</time></span>
              {t.subject && <span className="thread-subject" dir="auto">{t.subject}</span>}
              <span className="thread-preview" dir="auto">{t.last_message_preview}</span>
              <span className="thread-chips">
                <span className="src">{channelName[t.channel] ?? t.channel}</span>
                {t.mode === "human" && t.status !== "closed" ? <KindChip kind="human" label="With your team" /> : t.last_reply_kind && <KindChip kind={t.last_reply_kind} />}
                {t.status === "closed" && <span className="src">Closed</span>}
                {t.assignee_name && <span className="src">{t.assignee_name}</span>}
              </span>
            </Link>
          ))}
          </div>
        </aside>
        <section className="conversation">{threadId ? <Conversation key={threadId} id={threadId} base={base} orgId={orgId} /> : <div className="conversation-empty"><Icon name="inbox" /><p>Choose a conversation to read it here.</p></div>}</section>
      </div>
    </>
  );
}

const kindLabel: Record<string, string> = { ai: "AI reply", saved: "Saved answer", automation: "Automation", human: "Your team", notice: "SVENEX notice" };
const kindClass: Record<string, string> = { ai: "ai", saved: "saved", automation: "auto", human: "you", notice: "auto" };

/** KindChip shows how a reply was made, like the board on svenex.app. */
export function KindChip({ kind, label }: { kind: ReplyKind; label?: string }) {
  return <span className={`kchip ${kindClass[kind] ?? "auto"}`}>{label ?? kindLabel[kind] ?? kind}</span>;
}


// Today: the time; this week: the day; older: the date.
function shortTime(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (now.getTime() - d.getTime() < 6 * 86400_000) return d.toLocaleDateString("en-GB", { weekday: "short" });
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function Conversation({ id, base, orgId }: { id: string; base: string; orgId: string }) {
  const qc = useQueryClient();
  const key = [orgId, "thread", id];
  const thread = useQuery({
    ...threadQuery(orgId, base, id),
    refetchInterval: 4_000, // TODO(realtime): replace polling with SSE when inbox volume grows
  });
  const [body, setBody] = useState("");
  const [templating, setTemplating] = useState(false);
  const plan = useEntitlements();
  const canTemplate = plan.enabled("whatsapp_channel");
  const isWhatsApp = thread.data?.channel === "whatsapp";
  const templates = useQuery({
    queryKey: [orgId, "wa-templates"],
    queryFn: () => api<{ templates: WaTemplate[] }>(`${base}/whatsapp/templates`),
    enabled: isWhatsApp && canTemplate,
    staleTime: 60_000,
  });
  const { allows, memberId } = useOrg();
  const notes = useQuery({ queryKey: [orgId, "thread", id, "notes"], queryFn: () => api<{ notes: Note[] }>(`${base}/threads/${id}/notes`), refetchInterval: 15_000 });
  const members = useQuery({ queryKey: [orgId, "members"], queryFn: () => api<{ members: Member[] }>(`${base}/members`), enabled: allows("contacts_all") });
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [panel, setPanel] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const count = (thread.data?.messages.length ?? 0) + (notes.data?.notes.length ?? 0);
  useEffect(() => {
    // Scroll only the message list: scrollIntoView would move the whole page too.
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count]);
  // Opening a conversation (and new messages while it's open) marks it read.
  const inbound = thread.data?.messages.filter((m) => m.direction === "inbound").length;
  useEffect(() => {
    if (inbound === undefined) return;
    api(`${base}/threads/${id}/read`, { method: "POST" }).then(() => {
      qc.invalidateQueries({ queryKey: [orgId, "threads", "counts"] });
      qc.setQueriesData<{ threads: Thread[] }>({ queryKey: [orgId, "threads"] }, (d) => d && "threads" in d ? { ...d, threads: d.threads.map((x) => (x.id === id ? { ...x, unread: false } : x)) } : d);
    }).catch(() => { /* read markers are a convenience */ });
  }, [inbound, id, base, orgId, qc]);
  const addNote = useMutation({
    mutationFn: (text: string) => api<Note>(`${base}/threads/${id}/notes`, { method: "POST", json: { body: text } }),
    onSuccess: () => { setBody(""); qc.invalidateQueries({ queryKey: [orgId, "thread", id, "notes"] }); },
  });
  const delNote = useMutation({
    mutationFn: (noteId: string) => api(`${base}/threads/${id}/notes/${noteId}`, { method: "DELETE" }),
    onSettled: () => qc.invalidateQueries({ queryKey: [orgId, "thread", id, "notes"] }),
  });
  type Full = Thread & { messages: Message[] };
  // A sent reply shows in the conversation straight away (as "Sending…") and
  // the box clears; if it fails, it's taken back out and the text returns.
  const send = useMutation({
    mutationFn: (text: string) => api(`${base}/threads/${id}/messages`, { method: "POST", json: { body: text } }),
    onMutate: async (text) => {
      setBody("");
      await qc.cancelQueries({ queryKey: key, exact: true });
      const tmp: Message = { id: `pending-${Date.now()}`, direction: "outbound", sender_type: "member", reply_kind: "human", sender_name: null, status: "queued", body: text, created_at: new Date().toISOString() };
      qc.setQueryData<Full>(key, (d) => d && { ...d, messages: [...d.messages, tmp] });
      return { tmp };
    },
    onError: (_e, text, ctx) => {
      qc.setQueryData<Full>(key, (d) => d && { ...d, messages: d.messages.filter((m) => m.id !== ctx?.tmp.id) });
      setBody((b) => b || text);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: [orgId, "threads"] });
    },
  });
  // Take over, hand back, close and reopen change the header at once.
  const patch = useMutation({
    mutationFn: (json: Partial<Pick<Thread, "mode" | "status" | "assigned_member_id">> & { unassign?: boolean }) => api(`${base}/threads/${id}`, { method: "PATCH", json }),
    onMutate: async (json) => {
      await qc.cancelQueries({ queryKey: key, exact: true });
      const before = qc.getQueryData<Full>(key);
      qc.setQueryData<Full>(key, (d) => d && { ...d, ...json });
      return { before };
    },
    onError: (_e, _v, ctx) => { if (ctx?.before) qc.setQueryData(key, ctx.before); },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: [orgId, "threads"] });
    },
  });
  const submit = () => {
    const text = body.trim();
    if (!text) return;
    if (mode === "note") { if (!addNote.isPending) addNote.mutate(text); return; }
    if (!send.isPending) send.mutate(text);
  };
  if (thread.isLoading) return <Loading rows={5} />;
  if (thread.error) return <ErrorText error={thread.error} />;
  const t = thread.data!;
  const windowClosed = t.reply_window_closes_at != null && new Date(t.reply_window_closes_at) < new Date();
  return (
    <>
      <header className="conversation-head">
        <Link className="icon-btn back-btn" to={`/o/${orgId}/inbox`} aria-label="Back to conversations"><Icon name="back" /></Link>
        <span className="av" aria-hidden>{initials(who(t))}</span>
        <div className="conversation-who">
          <div className="inline"><b>{who(t)}</b><span className="src">{channelName[t.channel] ?? t.channel}</span></div>
          {t.subject && <div className="email-subject" dir="auto">{t.subject}</div>}
          <div className="muted small">
            {t.mode === "ai" ? "SVENEX is answering" : "Your team is answering"}{t.status === "closed" && " · closed"}
          </div>
        </div>
        <div className="actions">
          {allows("contacts_all") && !allows("readonly") ? (
            <select className="assign-select" aria-label="Assigned to" value={t.assigned_member_id ?? ""} onChange={(e) => patch.mutate(e.target.value ? { assigned_member_id: e.target.value } : { unassign: true })}>
              <option value="">Unassigned</option>
              {members.data?.members.filter((m) => m.status === "active" && m.crm_role).map((m) => <option key={m.id} value={m.id}>{m.id === memberId ? `Me (${m.display_name})` : m.display_name}</option>)}
            </select>
          ) : !allows("readonly") && t.assigned_member_id !== memberId ? (
            <button className="secondary" disabled={!!t.assigned_member_id} title={t.assigned_member_id ? `Assigned to ${t.assignee_name}` : undefined} onClick={() => patch.mutate({ assigned_member_id: memberId })}>{t.assigned_member_id ? t.assignee_name : "Assign to me"}</button>
          ) : <span className="muted small">{t.assignee_name ? `Assigned to ${t.assigned_member_id === memberId ? "you" : t.assignee_name}` : "Unassigned"}</span>}
          {t.contact_id && <button className={panel ? "" : "secondary"} aria-pressed={panel} onClick={() => setPanel(!panel)}>Contact</button>}
          {!allows("readonly") && (t.channel !== "email" || t.agent_id) && (t.mode === "ai" ? (
            <button className="secondary" onClick={() => patch.mutate({ mode: "human" })}>Take over</button>
          ) : (
            <button className="secondary" onClick={() => patch.mutate({ mode: "ai" })}>Hand back to AI</button>
          ))}
          {!allows("readonly") && (t.status !== "closed" ? (
            <button className="secondary" onClick={() => patch.mutate({ status: "closed" })}>Close</button>
          ) : (
            <button className="secondary" onClick={() => patch.mutate({ status: "open" })}>Reopen</button>
          ))}
        </div>
      </header>
      <div className={`conversation-body${panel && t.contact_id ? " with-panel" : ""}`}>
      <div className="messages" ref={list}>
        {timelineOf(t.messages, notes.data?.notes ?? []).map((item) => item.kind === "note" ? (
          <div key={item.note.id} className="msg note">
            <small className="note-label">Note for the team · only you and your team see this</small>
            <p dir="auto">{item.note.body}</p>
            <span className="msg-meta">
              <span>{item.note.author ?? "Someone"}</span>
              <time title={dateTime(item.note.created_at)}>{ago(item.note.created_at)}</time>
              {(item.note.mine || allows("contacts_all")) && !allows("readonly") && <button type="button" className="link small" onClick={() => confirm("Delete this note?") && delNote.mutate(item.note.id)}>Delete</button>}
            </span>
          </div>
        ) : ((m) => (
          <div key={m.id} className={`msg ${m.direction} ${m.sender_type}${m.reply_kind ? " k-" + m.reply_kind : ""}${m.id.startsWith("pending-") ? " pending" : ""}`}>
            {m.email?.subject && m.direction === "inbound" && <small className="muted email-from">{m.email.from_name ? `${m.email.from_name} <${m.email.from}>` : m.email.from}{m.email.has_attachments ? " · has attachments (open them in your mailbox)" : ""}</small>}
            {m.media && <MessageMedia media={m.media} path={`${base}/threads/${id}/messages/${m.id}/media`} />}
            <p dir="auto">{m.body}</p>
            <span className="msg-meta">
              {m.direction === "outbound" && m.reply_kind && <KindChip kind={m.reply_kind} label={m.reply_kind === "human" && m.sender_name ? m.sender_name : undefined} />}
              {m.direction === "outbound" && m.reply_kind === "saved" && <span>No new AI reply</span>}
              {m.template && <span>Template: {m.template}</span>}
              <time title={dateTime(m.created_at)}>{ago(m.created_at)}</time>
              {m.direction === "outbound" && t.channel !== "web" && <span className={`delivery ${m.status}`}>{statusLabel[m.status] ?? m.status}{m.error ? `: ${m.error}` : ""}</span>}
            </span>
          </div>
        ))(item.msg))}
      </div>
      {panel && t.contact_id && <ContactPanel contactId={t.contact_id} onClose={() => setPanel(false)} />}
      </div>
      <ErrorText error={send.error ?? patch.error ?? addNote.error ?? delNote.error} />
      {allows("readonly") ? (
        <p className="muted small composer-ro">You can read conversations, but replying needs a different role.</p>
      ) : t.channel === "email" ? (
        <div className="composer-email"><EmailComposer threadId={id} onSent={() => { qc.invalidateQueries({ queryKey: key }); }} /></div>
      ) : t.channel === "whatsapp" && (windowClosed || templating) ? (
        <div className="composer-template">
          {windowClosed && (
            <p className="notice small">
              {t.message_count === 0 || !t.messages.some((m) => m.direction === "inbound")
                ? "The customer hasn't written yet. WhatsApp only allows an approved template until they reply."
                : "More than 24 hours since the customer's last message. WhatsApp only allows an approved template until they reply."}
            </p>
          )}
          {canTemplate ? (
            <TemplateSender
              templates={(templates.data?.templates ?? []).filter((x) => x.connection_id === t.channel_connection_id)}
              firstName={t.contact_name?.split(" ")[0]}
              onCancel={windowClosed ? undefined : () => setTemplating(false)}
              onSend={async (templateId, params) => {
                await api(`${base}/threads/${id}/template-messages`, { method: "POST", json: { template_id: templateId, params } });
                setTemplating(false);
                qc.invalidateQueries({ queryKey: key });
                qc.invalidateQueries({ queryKey: [orgId, "threads"] });
              }}
            />
          ) : <p className="muted small">Message templates aren't included in your plan.</p>}
        </div>
      ) : (
        <div className="composer-wrap">
          <div className="composer-modes" role="group" aria-label="Write">
            <button type="button" className={mode === "reply" ? "on" : ""} aria-pressed={mode === "reply"} onClick={() => setMode("reply")}>Reply to customer</button>
            <button type="button" className={mode === "note" ? "on" : ""} aria-pressed={mode === "note"} onClick={() => setMode("note")}>Note for the team</button>
            {mode === "reply" && <span className="muted small">Type / for saved replies</span>}
          </div>
          {mode === "reply" && body.startsWith("/") && <SavedReplies query={body.slice(1)} arabic={/[\u0600-\u06FF]/.test(t.last_message_preview ?? "")} onPick={(text) => setBody(text)} />}
          <form className={`composer${mode === "note" ? " note-mode" : ""}`} onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
              placeholder={mode === "note" ? "Only your team sees notes. The customer and the AI never do." : t.mode === "ai" ? "Reply yourself, or let SVENEX handle it" : "Write a reply"} rows={2} maxLength={4000} aria-label={mode === "note" ? "Note for the team" : "Reply"} dir="auto" />
            <div className="composer-actions">
              <button type="submit" disabled={(mode === "note" ? addNote.isPending : send.isPending) || !body.trim()}>{mode === "note" ? "Add note" : "Send"}</button>
              {mode === "reply" && t.channel === "whatsapp" && canTemplate && <button type="button" className="secondary small" onClick={() => setTemplating(true)}>Use a template</button>}
            </div>
          </form>
        </div>
      )}
    </>
  );
}

const channelName: Record<string, string> = { whatsapp: "WhatsApp", web: "Web", email: "Email" };

function who(t: Thread) {
  return t.contact_name || t.contact_email || t.email_from || (t.channel === "whatsapp" ? "WhatsApp customer" : t.channel === "email" ? "Email" : "Website visitor");
}

const statusLabel: Record<string, string> = { queued: "Sending…", sent: "Sent", delivered: "Delivered", read: "Read", failed: "Not delivered" };

interface Note { id: string; body: string; member_id: string | null; author: string | null; mine: boolean; created_at: string }
type Item = { kind: "msg"; msg: Message; at: string } | { kind: "note"; note: Note; at: string };

/** timelineOf puts messages and team notes in the order they happened. */
function timelineOf(messages: Message[], notes: Note[]): Item[] {
  const items: Item[] = [...messages.map((m) => ({ kind: "msg" as const, msg: m, at: m.created_at })), ...notes.map((n) => ({ kind: "note" as const, note: n, at: n.created_at }))];
  return items.sort((a, b) => a.at.localeCompare(b.at));
}

/** SavedReplies: the company's saved answers, picked with "/" in the reply box. */
function SavedReplies({ query, arabic, onPick }: { query: string; arabic: boolean; onPick: (text: string) => void }) {
  const { base, orgId } = useOrg();
  const plan = useEntitlements();
  const q = useQuery({
    queryKey: [orgId, "saved-replies"],
    queryFn: () => api<{ replies: { id: string; question: string; answer: string; answer_ar: string | null }[] }>(`${base}/saved-answers/replies`),
    enabled: plan.enabled("saved_answers"),
    staleTime: 60_000,
  });
  if (!plan.enabled("saved_answers")) return <div className="saved-pop"><p className="muted small">Saved answers aren't part of your plan.</p></div>;
  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const list = (q.data?.replies ?? []).filter((r) => words.every((w) => (r.question + " " + r.answer).toLowerCase().includes(w))).slice(0, 6);
  return (
    <div className="saved-pop" role="listbox" aria-label="Saved replies">
      {q.isLoading && <p className="muted small">Loading…</p>}
      {q.data && list.length === 0 && <p className="muted small">{q.data.replies.length === 0 ? "No saved answers yet. Add them under Knowledge." : "No saved answer matches."}</p>}
      {list.map((r) => (
        <button key={r.id} type="button" role="option" aria-selected={false} onClick={() => onPick(arabic && r.answer_ar ? r.answer_ar : r.answer)}>
          <b dir="auto">{r.question}</b>
          <small className="muted" dir="auto">{(arabic && r.answer_ar ? r.answer_ar : r.answer).slice(0, 140)}</small>
        </button>
      ))}
    </div>
  );
}

/** ContactPanel: the customer's details beside the conversation. */
function ContactPanel({ contactId, onClose }: { contactId: string; onClose: () => void }) {
  const { base, orgId } = useOrg();
  const plan = useEntitlements();
  const c = useQuery({ queryKey: [orgId, "contact", contactId], queryFn: () => api<Contact & { can_edit: boolean }>(`${base}/contacts/${contactId}`) });
  const deals = useQuery({ queryKey: [orgId, "deals", "contact", contactId], queryFn: () => api<{ deals: { id: string; title: string; status: string; stage_name: string }[] }>(`${base}/deals?contact=${contactId}`), enabled: plan.enabled("deals") });
  return (
    <aside className="contact-panel" aria-label="Contact">
      <div className="card-head"><b>Contact</b><button className="icon-btn" aria-label="Close contact details" onClick={onClose}><Icon name="close" /></button></div>
      {c.isLoading ? <Loading /> : c.error ? <ErrorText error={c.error} /> : (
        <>
          <p className="contact-panel-name" dir="auto">{[c.data!.first_name, c.data!.last_name].filter(Boolean).join(" ") || c.data!.email || c.data!.phone}</p>
          <dl className="details stacked small">
            {c.data!.email && <><dt>Email</dt><dd>{c.data!.email}</dd></>}
            {c.data!.phone && <><dt>Phone</dt><dd dir="ltr">{c.data!.phone}</dd></>}
            <dt>Stage</dt><dd>{stageLabel[c.data!.lifecycle_stage] ?? c.data!.lifecycle_stage}</dd>
            <dt>Owner</dt><dd>{c.data!.owner_name ?? "Unassigned"}</dd>
            <dt>Score</dt><dd>{c.data!.lead_score} / 100</dd>
          </dl>
          {(c.data!.tags?.length ?? 0) > 0 && <div className="tag-row">{c.data!.tags!.map((x) => <span key={x} className="ctag">{x}</span>)}</div>}
          {(deals.data?.deals.length ?? 0) > 0 && (
            <>
              <p className="muted small" style={{ margin: ".9rem 0 .3rem" }}>Deals</p>
              <ul className="plain-list tight">{deals.data!.deals.slice(0, 5).map((d) => <li key={d.id} className="small"><Link to={`/o/${orgId}/deals/${d.id}`}>{d.title}</Link> <span className="muted">· {d.status === "open" ? d.stage_name : d.status === "won" ? "Won" : "Lost"}</span></li>)}</ul>
            </>
          )}
          <Link className="button-link secondary small" to={`/o/${orgId}/contacts/${contactId}`}>Open contact</Link>
        </>
      )}
    </aside>
  );
}
